/**
 * Z-index tokens for the **Clip Preview** surface, in stack order.
 *
 * Single source of truth so styled-components inside the clip-preview
 * surface (`SocialClipShareModal`, `ClipPreviewControlBar`,
 * `ClipDimensionsDropdown`, `CompositionCanvasActionsLayer`,
 * `CompositionQuickEditPopover`) stay in lockstep when we adjust modal
 * layering. Adding a new layer? Bump it here, not in the styled file.
 *
 * Stack order (highest paints on top). Values are intentionally packed in
 * the six-figure range that the host project already uses for modal
 * chrome; non-clip-preview layers (safe-zones) live in
 * [src/safe-zones/zIndex.ts](../../safe-zones/zIndex.ts), with their
 * relative positions documented here so a single read of this file
 * surfaces the whole stack.
 *
 *   1000005   CLIP_PREVIEW_Z.modalFullscreenBody   modal body in mobile / stretched layout
 *   1000007   CLIP_PREVIEW_Z.modalChrome           modal chrome (close X, dropdown backdrops)
 *   1000008   CLIP_PREVIEW_Z.modalPopover          modal popovers + dropdowns (share overlay, publish, dimensions)
 *   1000009   SAFE_ZONE_Z_INDEX.overlay            portaled safe-zone overlay (owned by safe-zones)
 *   1000010   CLIP_PREVIEW_Z.canvasActionLayer     floating pencil/menu above the iframe canvas (PLAT-9968)
 *   1000010   SAFE_ZONE_Z_INDEX.dropdownPanel      ⚠ COLLIDES with the canvas action layer — safe in practice (safe-zone picker and composition selection don't coexist), but worth flagging when either layer next moves
 *   1000011   SAFE_ZONE_Z_INDEX.toolbarTooltip     safe-zone hint tooltips (owned by safe-zones)
 *   1000012   CLIP_PREVIEW_Z.transcriptHoverPill   transcript-hover canvas-style pill (PLAT-10045) — portaled to document.body so it sits above the iframe canvas regardless of modal stacking context
 *   1000013   CLIP_PREVIEW_Z.annotationOverlay     annotation drawing surface over the player (PLAT-11288) — portaled to document.body; takes pointer input only while a tool is armed
 *   1000020   CLIP_PREVIEW_Z.quickEditPopover      composition text editor above the canvas action layer (PLAT-9968)
 *   1000021   CLIP_PREVIEW_Z.languageDropdownMenu  transcript-toolbar language menu, portaled to document.body — must clear the modal stack (PLAT-10216)
 *   1000022   CLIP_PREVIEW_Z.transcriptOverflowMenu transcript-toolbar ⋯ overflow menu, portaled to document.body — same modal-stack constraint as the language menu (PLAT-10646)
 *   1000022   CLIP_PREVIEW_Z.languageDubMicTooltip green-mic dub-info overlay, portaled to document.body — anchored inside the language menu, so it must clear it (PLAT-10732)
 *   1000023   CLIP_PREVIEW_Z.languageDropdownPopover  kebab/popover opened from inside the language menu (Music and Sounds actions) — above the menu and its sibling tooltips (PLAT-10627)
 *   1000023   CLIP_PREVIEW_Z.reelSectionContextMenu  Reel Editor per-Section right-click menu, portaled to document.body — must clear the modal stack (PLAT-11060)
 *   1000023   CLIP_PREVIEW_Z.reelChapterContextMenu  Reel Editor per-Chapter right-click menu — same tier, never coexists with the Section one (PLAT-11064)
 *   1000024   ENLARGED_HOVER_PREVIEW_Z (common/topLayerZ.ts)  enlarged media hover preview, portaled to document.body — must clear this modal stack and its video player (PLAT-11200)
 *   1000025   CLIP_PREVIEW_Z.translationEditorPopover  Fix Translation / Edit Translation editors, portaled to document.body so drag-to-select inside them can't autoscroll the transcript (PLAT-11484); lifted by --top-layer-context-menu-z inside the Translation Review workspace
 *   1000030   CLIP_PREVIEW_Z.translationReview     Translation Review workspace launched from the clip preview — clears the whole stack above (PLAT-10286)
 *   1000035   PUBLISH_PROGRESS_DRAWER_Z (common/topLayerZ.ts)  persistent top-right publish progress drawer — visible above every full-surface layer here (PLAT-10703)
 *   1000040+  TOP_LAYER_Z (common/topLayerZ.ts)    context menus / speaker modals / tooltips — transient overlays above EVERY full-surface layer, incl. this one
 */
export const CLIP_PREVIEW_Z = {
  /** Modal body in full-screen / mobile-stretched layout. */
  modalFullscreenBody: 1000005,
  /** Modal chrome — close X button, backdrops behind nested dropdowns. */
  modalChrome: 1000007,
  /** Modal popovers + dropdowns (share overlay, publish dropdown, preview-mode dropdown, dimensions dropdown). */
  modalPopover: 1000008,
  /** Canvas action layer — floating pencil/menu above the iframe canvas (PLAT-9968). */
  canvasActionLayer: 1000010,
  /**
   * Annotation drawing surface over the player (PLAT-11288). Portaled to
   * `document.body` like the safe-zone overlay, so it clears the iframe's
   * stacking context. Above `canvasActionLayer` because arming a tool
   * deliberately takes pointer input from the canvas — though in practice the
   * two never coexist, since the canvas action layer is suppressed while a
   * drawing tool is armed. Below `quickEditPopover` for the same reason.
   */
  annotationOverlay: 1000013,
  /**
   * Overlay range-adjust drag handles on the transcript underline. Portaled
   * to `document.body` so they clear the modal's stacking context, but kept
   * BELOW the hover pill + quick-edit popover so those always paint on top of
   * the handles (they don't coexist in practice — entering adjust dismisses
   * the pill — but ordering must be correct if they ever overlap).
   */
  overlayRangeHandles: 1000011,
  /**
   * Transcript-hover canvas-style pill (PLAT-10045). Portaled to
   * `document.body` so it sits above the iframe canvas regardless of
   * the modal's stacking context.
   */
  transcriptHoverPill: 1000012,
  /** Quick-edit popover — composition text editor above the canvas action layer (PLAT-9968). */
  quickEditPopover: 1000020,
  /**
   * Transcript-toolbar language menu (`TranslationToggle`). The menu is
   * portaled to `document.body` by `useAnchoredDropdown`, which assigns a
   * ~100k z-index that sits BELOW this clip-preview modal stack — so inside
   * the clip preview the dropdown rendered behind the modal. Override it to
   * the top of the stack so the menu is usable above the modal. Harmless in
   * Creator Studio (nothing else reaches this range and the menu
   * auto-dismisses). (PLAT-10216)
   */
  languageDropdownMenu: 1000021,
  /**
   * Transcript-toolbar ⋯ overflow menu (`TranscriptOverflowMenu`,
   * PLAT-10646). Portaled to `document.body`; the shared
   * `ContextMenuSurface` resting z (~100k) sits below the clip-preview
   * modal stack, so inside the clip preview the menu opened behind the
   * modal. Same constraint and fix as the language dropdown above.
   * Harmless in Creator Studio.
   */
  transcriptOverflowMenu: 1000022,
  /**
   * Green-mic dub-info overlay (`TranslationToggle`, PLAT-10732). The
   * react-tooltip is portaled to `document.body` and anchored to a mic that
   * lives INSIDE the language dropdown menu, so it must sit one step above
   * `languageDropdownMenu` or it renders behind the menu (the shared
   * `DARK_TOOLTIP_STYLE` z of 100005 sits far below this range). Kept below
   * `translationReview` — the menu itself is already below that, so the mic
   * (and its overlay) never coexist above a review workspace.
   */
  languageDubMicTooltip: 1000022,
  /**
   * A kebab/popover opened from *inside* the language menu — e.g. the
   * "Music and Sounds" actions menu (`MusicActionsMenu`, PLAT-10627). It portals
   * to `document.body`, so without this it would inherit the shared context-menu
   * resting z (100001) and open *behind* the language dropdown it belongs to.
   * One step above `languageDubMicTooltip` so it also clears the sibling
   * overlays anchored inside that same menu.
   */
  languageDropdownPopover: 1000023,
  /**
   * The Reel Editor's per-Section right-click menu
   * (`ReelSectionContextMenu`, PLAT-11060). Portaled to `document.body`; the
   * shared `ContextMenuSurface` resting z (100001) sits below the clip-preview
   * modal stack, so without this the menu opens behind the modal it was
   * right-clicked in. The panel's `InsertDivider` menu clears the backdrop by
   * way of `useAnchoredDropdown`, which cannot position from a pointer — this
   * puts both menus on one documented tier rather than two accidents.
   */
  reelSectionContextMenu: 1000023,
  /**
   * The Reel Editor's per-Chapter right-click menu (`ReelChapterContextMenu`,
   * PLAT-11064) — same tier and same constraints as the Section menu above. The
   * two never coexist, so a shared value is correct; they get separate tokens
   * because a token named for Sections used by a Chapter menu is exactly the sort
   * of thing that misleads the next reader of this stack.
   */
  reelChapterContextMenu: 1000023,
  /**
   * The Translation Review workspace opened from the clip preview (T3.3 /
   * PLAT-10286). Must clear the entire clip-preview stack above so the
   * full-surface review modal covers the clip preview it was launched from.
   * The shared transient overlays it hosts (speaker ⋯ menu, Add/Edit modal,
   * tooltips) sit on the TOP_LAYER_Z scale (1000040+), above this.
   */
  translationReview: 1000030,
  /**
   * The transcript's translation editors — Fix Translation
   * (`ReplaceSpanEditor`) and Edit Translation (`TranslatedPhraseEditorPopover`),
   * PLAT-11484 / PLAT-11479. Portaled to `document.body`: rendered inline they
   * sat inside the transcript scroll container, and because the clip-preview
   * modal wrapper's `backdrop-filter` makes it the containing block for fixed
   * descendants, Chrome's drag-to-select autoscroll walked up from the editor's
   * textarea to that scroller and scrolled the transcript behind the popover.
   * Above `ENLARGED_HOVER_PREVIEW_Z` (1000024) so an open editor is never
   * covered by a hover card; below `translationReview` (1000030), inside which
   * the same editors are lifted via `--top-layer-context-menu-z` instead.
   */
  translationEditorPopover: 1000025,
} as const;
